// import api's and mutations
import {
  downloadReport,
  getAnalyzerData
} from 'RestAPIs/capacity-plan/analyzer';

// import atoms
import { scenarioAtom } from '../atoms';
import {
  compareWithAtom,
  groupByAtom,
  initialVisibleQuantitiesAtom,
  quantitiesAtom,
  selectedCategoriesAtom,
  selectedFunctionsAtom,
  visibleQuantitiesAtom
} from './atoms';

// import components
import {
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  styled,
  Tooltip
} from '@mui/material';
import { LoadingOverlay } from 'components/shared';
import Styles from './Styles';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';
import {
  BlueSwitch,
  DarkSwitch,
  PrimarySwitch,
  YellowSwitch
} from 'components/shared/Switch/Switch.components';
import NoScenario from '../components/NoScenario';
import Chart from './components/Chart';
import SettingsSection from './components/SettingsSection/SettingsSection';
import Table from './components/Table';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useRecoilState } from 'recoil';

// import types
import { Quantity, QuantityName } from './types';

// import constants and enums
import { CAPPLAN_TABS } from '../enums';
import { GroupBy, Quantities } from './constants';

// import utils
import { groupByKey } from '../utils';
import { getTotals, ObjectKeys } from './utils';
import { Icon } from '@sdb/planning-ui';

const StyledFormGroup = styled(FormGroup)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 0 12px;
  margin: 0 auto;
`;

type Props = {
  showDetailsSubTab: (tab: keyof typeof CAPPLAN_TABS) => void;
};

const Analyzer = ({ showDetailsSubTab }: Props) => {
  const { t } = useTranslation('common');
  const [quantities, setQuantities] = useRecoilState(quantitiesAtom);
  const [defaultQuantities, setDefaultQuantities] = useState<Quantity[]>([]);
  const [visibleQuantities, setVisibleQuantities] = useRecoilState(
    visibleQuantitiesAtom
  );
  const [initialVisibleQuantities, setInitialVisibleQuantities] =
    useRecoilState(initialVisibleQuantitiesAtom);

  const [scenario] = useRecoilState(scenarioAtom);
  const [groupBy] = useRecoilState(groupByAtom);
  const [selectedCategories] = useRecoilState(selectedCategoriesAtom);
  const [compareWith] = useRecoilState(compareWithAtom);
  const [selectedFunctions] = useRecoilState(selectedFunctionsAtom);

  const { year, aggregate, interval } = useYearQuarterNavigation();

  const { isLoading, refetch } = useQuery(
    ['analyzerData', scenario?.scenarioId, year, aggregate],
    async ({ signal }) => {
      if (!scenario?.scenarioId) {
        return Promise.reject(new Error('No scenario ID'));
      }

      const { scenarioId } = scenario;
      const data = await getAnalyzerData({
        scenarioId,
        year,
        aggregateOn: aggregate,
        signal
      });

      const categories = ObjectKeys(data);
      const values = Object.values(data);

      if (values[0].length === 0) {
        setVisibleQuantities([Quantities.FORMATION, Quantities.ROSTER]);
        setInitialVisibleQuantities([Quantities.FORMATION, Quantities.ROSTER]);
      } else {
        setVisibleQuantities([Quantities.WORK_DEMAND, Quantities.ROSTER]);
        setInitialVisibleQuantities([
          Quantities.WORK_DEMAND,
          Quantities.ROSTER
        ]);
      }

      const quantities = values.map((value, i) => ({
        name: categories[i],
        scenarioId,
        data: value,
        totals: getTotals(value, aggregate)
      }));

      const defaultQuantities = quantities.map((quantity) => {
        let { data } = quantity;
        data = groupByKey(data, 'openDuty', t).sort(
          (a, b) => b.openDuty - a.openDuty
        );
        return {
          ...quantity,
          data
        };
      });

      setQuantities(quantities);
      setDefaultQuantities(defaultQuantities);
    },
    {
      enabled: Boolean(scenario?.scenarioId)
    }
  );

  useEffect(() => {
    if (scenario) {
      refetch();
    }
  }, [scenario, aggregate, year]);

  const quantitiesByFilters = useMemo(() => {
    const filtersActive =
      !!selectedCategories.length ||
      !!selectedFunctions.length ||
      !!compareWith ||
      !!groupBy;
    const quantitiesToFilter = filtersActive ? quantities : defaultQuantities;

    // apply the same filters + period slicing used for the main series
    const applyAllFilters = (quantity: Quantity) => {
      let { data } = quantity;

      if (selectedCategories.length || selectedFunctions.length) {
        data = data.filter(
          ({ category = t('unknown'), functionDescription = t('unknown') }) => {
            const hasCategoriesFilter = !!selectedCategories.length;
            const hasFunctionsFilter = !!selectedFunctions.length;

            if (hasCategoriesFilter && hasFunctionsFilter) {
              return (
                selectedCategories.find((option) => option.name === category) &&
                selectedFunctions.find(
                  (option) => option.name === functionDescription
                )
              );
            }
            if (hasCategoriesFilter)
              return !!selectedCategories.find(
                (option) => option.name === category
              );
            if (hasFunctionsFilter)
              return !!selectedFunctions.find(
                (option) => option.name === functionDescription
              );
            return true;
          }
        );
      }

      if (groupBy) {
        const key = groupBy === GroupBy.CATEGORY ? 'category' : 'functionId';
        data = groupByKey(data, key, t);
      }

      // period slicing
      data = data.map((entry) => ({
        ...entry,
        periods: entry.periods.slice(
          interval.start,
          interval.end || entry.periods.length
        )
      }));

      const totals = getTotals(data, aggregate);
      return { data, totals };
    };

    let compareTotalsFiltered: Array<{ total: number }> | null = null;
    if (compareWith) {
      const rawCompare =
        quantitiesToFilter.find((quantity) => quantity.name === compareWith) ??
        quantities.find((quantity) => quantity.name === compareWith);

      if (rawCompare) {
        compareTotalsFiltered = applyAllFilters(rawCompare).totals;
      }
    }

    // main projection for visible series
    const filteredQuantities = quantitiesToFilter
      .filter(({ name }) => visibleQuantities.includes(name))
      .map((quantity) => {
        const { data, totals } = applyAllFilters(quantity);

        // subtract ONLY if we have filtered compare totals and this is not the compare series
        let adjustedTotals = totals;
        if (compareTotalsFiltered && quantity.name !== compareWith) {
          adjustedTotals = totals.map((value, i) => ({
            ...value,
            total: value.total - (compareTotalsFiltered?.[i]?.total ?? 0)
          }));
        }

        return { ...quantity, data, totals: adjustedTotals };
      });

    return filteredQuantities;
  }, [
    quantities,
    defaultQuantities,
    interval,
    visibleQuantities,
    groupBy,
    selectedCategories,
    selectedFunctions,
    compareWith,
    aggregate,
    t
  ]);

  const toggleQuantity = (quantity: QuantityName) => {
    const hasQuantity = visibleQuantities.includes(quantity);

    setVisibleQuantities(
      hasQuantity
        ? visibleQuantities.filter((q) => q !== quantity)
        : [...visibleQuantities, quantity]
    );

    // Remember user toggled settings for setting back the old values after mutations caused by compare with select
    setInitialVisibleQuantities(
      hasQuantity
        ? initialVisibleQuantities.filter((q) => q !== quantity)
        : [...initialVisibleQuantities, quantity]
    );
  };

  useEffect(() => {
    setVisibleQuantities(
      initialVisibleQuantities.filter((q) => q !== compareWith)
    );
  }, [compareWith]);

  const nonGroupByFiltersActive =
    !!selectedCategories.length || !!selectedFunctions.length || !!compareWith;

  const onDownloadClick = async () => {
    if (scenario?.scenarioId) {
      downloadReport({
        scenarioId: scenario.scenarioId,
        year,
        aggregateOn: aggregate
      });
    }
  };

  if (!scenario) {
    return <NoScenario />;
  }

  return (
    <Styles>
      <LoadingOverlay loading={isLoading} />
      <Box mb={4} position="relative" display="flex" alignItems="center">
        <Box display="flex" alignItems="center" ml="auto" gap={1}>
          <SettingsSection />
          <Tooltip title="Download rapport">
            <IconButton onClick={onDownloadClick} size="large">
              <Icon type="file-xls" color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <div className="d-flex" style={{ marginBottom: 20 }}>
        <StyledPaper className="main">
          <Chart
            quantities={quantitiesByFilters}
            shouldStack={!!groupBy || !nonGroupByFiltersActive}
            isLoading={isLoading}
          />
          <StyledFormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={<DarkSwitch groupBy={!!groupBy} />}
              label={`${t('analyzer.filters.workDemand')}`}
              checked={visibleQuantities.includes(Quantities.WORK_DEMAND)}
              disabled={compareWith === Quantities.WORK_DEMAND}
              onChange={() => {
                toggleQuantity(Quantities.WORK_DEMAND);
              }}
            />
            <FormControlLabel
              control={<BlueSwitch groupBy={!!groupBy} />}
              label={`${t('analyzer.filters.basePlan')}`}
              checked={visibleQuantities.includes(Quantities.BASE_PLAN)}
              disabled={compareWith === Quantities.BASE_PLAN}
              onChange={() => {
                toggleQuantity(Quantities.BASE_PLAN);
              }}
            />
            <FormControlLabel
              control={<YellowSwitch groupBy={!!groupBy} />}
              label={`${t('analyzer.filters.formation')}`}
              checked={visibleQuantities.includes(Quantities.FORMATION)}
              onChange={() => {
                toggleQuantity(Quantities.FORMATION);
              }}
            />
            <FormControlLabel
              control={<PrimarySwitch groupBy={!!groupBy} />}
              label={`${t('analyzer.filters.roster')}`}
              checked={visibleQuantities.includes(Quantities.ROSTER)}
              disabled={compareWith === Quantities.ROSTER}
              onChange={() => {
                toggleQuantity(Quantities.ROSTER);
              }}
            />
          </StyledFormGroup>
        </StyledPaper>
      </div>
      <Table
        quantities={quantitiesByFilters}
        showDetailsSubTab={showDetailsSubTab}
      />
    </Styles>
  );
};

export default Analyzer;
